export const ProfileEvent = {
  AccountInfoTypeClick: 'account_info_type_click',
  AccountProfileClick: 'account_profile_click',
  UIDCopyClick: 'uid_copied',
  ProfilePageView: 'profile_page_view',
  ProfilePageTabClick: 'profile_page_tab_click',
  EditProfileClick: 'edit_profile_click',
  ProfilePageSettingClick: 'profile_page_setting_click',
  SocialLandingClick: 'social_landing_click',
  EditProfileSaveClick: 'edit_profile_save_click',
  SwitchPublicButtonClick: 'switch_public_button_click',
  ProfileSettingsDoneClick: 'profile_settings_done_click',
  SwitchToPublicLinkClick: 'switch_to_public_link_click',
  SwitchPublicModalView: 'switch_public_modal_view',

  // performance
  ProfilePagePeriodsClick: 'profile_page_periods_click',
  SelectPeriodClick: 'select_period_click',
  TopPnlPositionsInfoClick: 'top_pnl_positions_info_click',
  TopPnlPositionClick: 'top_pnl_position_click',
  ProfilePageTradeNowClick: 'profile_page_trade_now_click',
  ProfilePageSwitchToPublicClick: 'profile_page_switch_to_public_click',

  // trading activity
  TradeHistoryPositionClick: 'trade_history_position_click',
  TradingActivityPositionTypeClick: 'trading_activity_position_type_click',

  // feed
  ProfilePageFeedMenuClick: 'profile_page_feed_menu_click',
  ProfilePageFeedReadMoreClick: 'profile_page_feed_read_more_click',
  ProfilePageFeedEditClick: 'profile_page_feed_edit_click',
  ProfilePageFeedDeleteClick: 'profile_page_feed_delete_click',
  ProfilePageFeedPostClick: 'profile_page_feed_post_click',
  ProfilePageFeedScrolled: 'profile_page_feed_scrolled',
  ProfilePageFeedEndScrolled: 'profile_page_feed_end_scrolled',

  // follow
  FollowModalView: 'follow_modal_view',
  FollowModalUserFollowClick: 'follow_modal_user_follow_click',
  UserFollowHistoryClick: 'user_follow_history_click',
  ProfilePageUserFollowClick: 'profile_page_user_follow_click',
  FollowingAlertClick: 'following_alert_click',
  FollowingNotificationModalView: 'following_notification_modal_view',
  FollowingNotificationApplyClick: 'following_notification_apply_click',

  // edit profile
  EditProfilePageView: 'edit_profile_page_view',
  EditProfilePageDisconnectXClick: 'edit_profile_page_disconnect_x_click',
  EditProfilePageDisconnectXCancelClick: 'edit_profile_page_disconnect_x_cancel_click',
  EditProfilePageDisconnectXConfirmClick: 'edit_profile_page_disconnect_x_confirm_click',
  EditProfilePageConnectXClick: 'edit_profile_page_connect_x_click',
  EditProfilePageXConnectionStatus: 'edit_profile_page_x_connection_status',
} as const;

type AccountInfo = 'vip_status' | 'account' | 'referral' | 'logout';
type InfoStatus = 'public' | 'private';
type FollowType = 'following' | 'followers';
type ProfileFeedSegmentPayload = {
  profile_id?: string;
  is_my_profile: boolean;
  list_order: number;
};

export interface ProfileEventDataType {
  [ProfileEvent.AccountInfoTypeClick]: {
    account_info: AccountInfo;
    is_kol: boolean;
  };
  [ProfileEvent.AccountProfileClick]: {
    vip_status: boolean;
    username: string;
  };
  [ProfileEvent.UIDCopyClick]: {
    is_menu?: boolean;
  };
  [ProfileEvent.ProfilePageView]: {
    profile_id?: string;
    vip_status: boolean;
    info_status: InfoStatus;
    weekly_trader: boolean;
    profile_ranking?: number;
    is_my_profile: boolean;
  };
  [ProfileEvent.ProfilePageTabClick]: {
    tab_type: string;
    profile_id?: string;
    is_my_profile: boolean;
    is_new: boolean;
  };
  [ProfileEvent.EditProfileClick]: {
    vip_status: boolean;
    info_status: InfoStatus | null;
    ranking?: number;
    weekly_trader: boolean;
    username: string;
  };
  [ProfileEvent.ProfilePageSettingClick]: {
    vip_status: boolean;
    ranking?: number;
    weekly_trader: boolean;
  };
  [ProfileEvent.SocialLandingClick]: {
    vip_status: boolean;
    info_status: InfoStatus | null;
    weekly_trader: boolean;
    profile_id?: string;
    profile_username?: string;
    profile_ranking?: string;
    /** @example X, TELEGRAM */
    social: string;
  };
  [ProfileEvent.SwitchPublicButtonClick]: {
    button_text: string;
  };
  [ProfileEvent.ProfileSettingsDoneClick]: {};
  [ProfileEvent.SwitchToPublicLinkClick]: {};

  // performance
  [ProfileEvent.ProfilePagePeriodsClick]: {
    profile_id?: string;
    profile_username?: string;
    profile_ranking?: string;
  };
  [ProfileEvent.SelectPeriodClick]: {
    profile_id?: string;
    profile_username?: string;
    profile_ranking?: string;
    period?: 'last_3day' | 'last_week' | 'last_month';
  };
  [ProfileEvent.TopPnlPositionsInfoClick]: {
    profile_id?: string;
    profile_username?: string;
    profile_ranking?: string;
  };
  [ProfileEvent.TopPnlPositionClick]: {
    profile_id?: string;
    profile_username?: string;
    profile_ranking?: string;
    side: 'buy' | 'sell';
    symbol: string;
    realized_pnl: number;
    realized_pnl_percentage: number;
  };
  [ProfileEvent.ProfilePageTradeNowClick]: {
    vip_status: boolean;
    weekly_trader: boolean;
    ranking?: number;
    tab_info: 'performance' | 'trade_history';
  };
  [ProfileEvent.ProfilePageSwitchToPublicClick]: {
    vip_status: boolean;
    weekly_trader: boolean;
    ranking?: number;
    tab_info: 'performance' | 'trade_history';
  };

  // trading activity
  [ProfileEvent.TradeHistoryPositionClick]: {
    profile_id?: string;
    profile_username?: string;
    position_id: string;
    side: 'buy' | 'sell';
    symbol: string;
    profile_ranking?: string;
    leverage: number;
    position_status: 'open' | 'closed';
    quantity: number;
    realized_pnl?: number;
    realized_pnl_percentage?: number;
  };
  [ProfileEvent.TradingActivityPositionTypeClick]: {
    position_status: 'open' | 'closed';
  };

  // feed
  [ProfileEvent.ProfilePageFeedMenuClick]: ProfileFeedSegmentPayload;
  [ProfileEvent.ProfilePageFeedReadMoreClick]: ProfileFeedSegmentPayload;
  [ProfileEvent.ProfilePageFeedEditClick]: ProfileFeedSegmentPayload;
  [ProfileEvent.ProfilePageFeedDeleteClick]: ProfileFeedSegmentPayload;
  [ProfileEvent.ProfilePageFeedPostClick]: ProfileFeedSegmentPayload;
  [ProfileEvent.ProfilePageFeedScrolled]: ProfileFeedSegmentPayload;
  [ProfileEvent.ProfilePageFeedEndScrolled]: ProfileFeedSegmentPayload;

  // follow
  [ProfileEvent.FollowModalView]: {
    follow_type: FollowType;
  };
  [ProfileEvent.FollowModalUserFollowClick]: {
    follow_type: FollowType;
    button_text: string;
    profile_id?: string;
    profile_username?: string;
    profile_ranking?: string;
  };
  [ProfileEvent.UserFollowHistoryClick]: {
    follow_type: FollowType;
    profile_id?: string;
    profile_username?: string;
    profile_ranking?: string;
    vip_status: boolean;
  };
  [ProfileEvent.ProfilePageUserFollowClick]: {
    button_text: string;
    profile_id?: string;
    profile_username?: string;
    profile_ranking?: string;
  };
  [ProfileEvent.FollowingAlertClick]: {
    profile_id?: string;
    profile_username?: string;
    profile_ranking?: string;
  };
  [ProfileEvent.FollowingNotificationModalView]: {
    new_post: boolean;
    trading: boolean;
    profile_id: string;
    profile_ranking?: string;
  };

  // edit profile
  [ProfileEvent.EditProfilePageView]: {
    telegram?: string;
    connected_x: boolean;
  };
  [ProfileEvent.EditProfilePageDisconnectXClick]: {};
  [ProfileEvent.EditProfilePageDisconnectXCancelClick]: {};
  [ProfileEvent.EditProfilePageDisconnectXConfirmClick]: {};
  [ProfileEvent.EditProfilePageConnectXClick]: { x?: string; telegram?: string };
  [ProfileEvent.EditProfilePageXConnectionStatus]: { is_success: boolean };
  [ProfileEvent.EditProfileSaveClick]: {
    connected_x: boolean;
    telegram?: string;
    username: string;
  };
}
